import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { CreditCard, Smartphone, Building2, Shield, Lock } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useVatRate } from "@/hooks/useVatRate";

export default function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { vatRate, vatEnabled, calculateVat } = useVatRate();
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [processing, setProcessing] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Get product ID and seller ID from URL params (for WhatsApp status links)
  const productId = searchParams.get("productId");
  const sellerId = searchParams.get("sellerId");

  useEffect(() => {
    const loadCartItems = async () => {
      // Check if coming from WhatsApp status link with product ID
      if (productId && sellerId) {
        try {
          // Use secure RPC to avoid exposing seller_id
          const { data: rpcData, error } = await supabase.rpc('get_public_product_by_id', {
            p_product_id: productId
          });
          const product = rpcData?.[0] || null;
          if (!product) throw new Error('Product not found');

          if (error) throw error;

          if (product) {
            // Set single product as cart item
            setCartItems([{
              id: product.id,
              name: product.title,
              price: product.price,
              quantity: 1,
              sellerId: '' // Hidden from public queries
            }]);
            
            toast({
              title: "Product Added",
              description: `${product.title} has been added to checkout`,
            });
          }
        } catch (error) {
          console.error('Error loading product:', error);
          toast({
            title: "Error",
            description: "Could not load product details",
            variant: "destructive"
          });
        }
      } else {
        // Get cart data from location state or use mock data
        const items = location.state?.cartItems || [
          { id: 1, name: "Wireless Headphones", price: 12500, quantity: 1 },
          { id: 2, name: "Phone Case", price: 2500, quantity: 2 },
        ];
        setCartItems(items);
      }
      setLoading(false);
    };

    loadCartItems();
  }, [productId, sellerId, location.state, toast]);

  const subtotal = cartItems.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0);
  const deliveryFee = 1500;
  const vat = vatEnabled ? calculateVat(subtotal) : 0; // VAT on subtotal only (exclude delivery)
  const transactionFee = Math.round((subtotal + deliveryFee + vat) * 0.015); // 1.5% transaction fee
  const total = subtotal + deliveryFee + vat + transactionFee;

  useEffect(() => {
    // Check for payment reference in URL params (for redirect from Paystack)
    const params = new URLSearchParams(location.search);
    const reference = params.get("reference");
    
    if (reference) {
      verifyPayment(reference);
    }

    // Get user's email if logged in
    const fetchUserEmail = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setEmail(user.email);
      }
    };
    fetchUserEmail();
  }, [location]);

  const verifyPayment = async (reference: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("verify-payment", {
        body: { reference }
      });

      if (error) throw error;

      if (data.payment_status === "success") {
        toast({
          title: "Payment Successful",
          description: "Your payment has been confirmed!",
        });
        navigate("/payment-success", { state: { paymentData: data } });
      } else {
        toast({
          title: "Payment Failed",
          description: "Your payment could not be processed.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      toast({
        title: "Verification Error",
        description: "Could not verify payment status.",
        variant: "destructive"
      });
    }
  };

  const handlePayment = async () => {
    if (!email || !phone || !fullName) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Create order in database
      const { data: order, error: orderError } = await supabase
        .from("orders")
        .insert({
          buyer_id: user?.id,
          seller_id: location.state?.sellerId || null,
          total_amount: total,
          items: cartItems,
          payment_method: "paystack",
          currency: "NGN",
          shipping_address: {
            fullName,
            phone,
            email
          }
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Initialize Paystack payment
      const { data, error } = await supabase.functions.invoke("initialize-payment", {
        body: {
          amount: total,
          email: email,
          orderId: order.id,
          metadata: {
            phone: phone,
            fullName: fullName,
            paymentMethod: paymentMethod,
            userId: user?.id
          }
        }
      });

      if (error) throw error;

      // Redirect to Paystack checkout
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast({
        title: "Payment Error",
        description: "Could not initialize payment. Please try again.",
        variant: "destructive"
      });
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="animate-pulse text-lg">Loading checkout...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold">Checkout</h1>
            <p className="text-muted-foreground mt-1">Complete your order</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Payment Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Payment Method */}
              <Card>
                <CardHeader>
                  <CardTitle>Payment Method</CardTitle>
                  <CardDescription>Choose how you'd like to pay</CardDescription>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <RadioGroupItem value="card" id="card" />
                        <Label htmlFor="card" className="flex items-center gap-3 cursor-pointer flex-1">
                          <CreditCard className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium">Credit/Debit Card</p>
                            <p className="text-sm text-muted-foreground">Visa, Mastercard, Verve</p>
                          </div>
                        </Label>
                      </div>

                      <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <RadioGroupItem value="bank" id="bank" />
                        <Label htmlFor="bank" className="flex items-center gap-3 cursor-pointer flex-1">
                          <Building2 className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium">Bank Transfer</p>
                            <p className="text-sm text-muted-foreground">Direct bank payment</p>
                          </div>
                        </Label>
                      </div>

                      <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer">
                        <RadioGroupItem value="ussd" id="ussd" />
                        <Label htmlFor="ussd" className="flex items-center gap-3 cursor-pointer flex-1">
                          <Smartphone className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium">USSD</p>
                            <p className="text-sm text-muted-foreground">Pay with USSD code</p>
                          </div>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>

              {/* Card Details (shown when card is selected) */}
              {paymentMethod === "card" && (
                <Card>
                  <CardHeader>
                    <CardTitle>Card Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="cardNumber">Card Number</Label>
                      <Input
                        id="cardNumber"
                        placeholder="1234 5678 9012 3456"
                        className="mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="expiry">Expiry Date</Label>
                        <Input
                          id="expiry"
                          placeholder="MM/YY"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cvv">CVV</Label>
                        <Input
                          id="cvv"
                          placeholder="123"
                          type="password"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="cardName">Cardholder Name</Label>
                      <Input
                        id="cardName"
                        placeholder="John Doe"
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Billing Address */}
              <Card>
                <CardHeader>
                  <CardTitle>Billing Address</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input 
                      id="fullName" 
                      placeholder="John Doe" 
                      className="mt-1" 
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="john@example.com" 
                      className="mt-1"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input 
                      id="phone" 
                      placeholder="+234 801 234 5678" 
                      className="mt-1"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Street Address</Label>
                    <Input id="address" placeholder="123 Main Street" className="mt-1" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input id="city" placeholder="Lagos" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input id="state" placeholder="Lagos State" className="mt-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Items */}
                  <div className="space-y-3">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {item.name} x {item.quantity}
                        </span>
                        <span className="font-medium">₦{(item.price * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {/* Totals */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>₦{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Delivery</span>
                      <span>₦{deliveryFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>VAT ({vatRate}%)</span>
                      <span>₦{Math.round(vat).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Transaction Fee (1.5%)</span>
                      <span>₦{transactionFee.toLocaleString()}</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="text-xl font-bold text-primary">₦{total.toFixed(0).toLocaleString()}</span>
                  </div>

                  {/* Security Badge */}
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                    <Shield className="h-4 w-4 text-primary" />
                    <span className="text-xs">Secure payment powered by Paystack</span>
                  </div>

                  {/* Pay Button */}
                  <Button 
                    variant="gradient" 
                    className="w-full"
                    size="lg"
                    onClick={handlePayment}
                    disabled={processing}
                  >
                    {processing ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Pay ₦{total.toFixed(0).toLocaleString()}
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    By completing this purchase you agree to our Terms of Service and Privacy Policy
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}